import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { finalize, from, map, Observable, of, throwError } from 'rxjs';
import { environment } from '@environments/environment';

interface CustomWindow extends Window {
  googleMapsReady?: () => void;
  google?: typeof google;
}

// API response types
interface GoogleApiStatus {
  status: 'OK' | 'ERROR';
  message: string;
  timestamp: string;
  googleApiKey: 'Configured' | 'Not configured' | 'Using free tier';
}

// Place Details interface
export interface PlaceDetails {
  place_id: string;
  formatted_address: string;
  address_components: {
    longText: string;
    shortText: string;
    types: string[];
    languageCode: string;
  }[];
  location?: {
    lat: number;
    lng: number;
  };
  unit_number?: string;
}

// Place Prediction interface
export interface PlacePrediction {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
  types: string[];
}

// Google Places API response interfaces
interface GoogleAutocompleteResponse {
  predictions: {
    place_id: string;
    description: string;
    structured_formatting: {
      main_text: string;
      secondary_text: string;
    };
    types: string[];
  }[];
  status: string;
}

interface GooglePlaceDetailsResponse {
  result: {
    place_id: string;
    formatted_address: string;
    address_components: {
      long_name: string;
      short_name: string;
      types: string[];
    }[];
    geometry: {
      location: {
        lat: number;
        lng: number;
      };
    };
  };
  status: string;
}

const AUTOCOMPLETE_MIN_QUERY_LENGTH = 3;

export interface MapLocation {
  lat: number | null;
  lng: number | null;
  label: string;
  type: string; // Type of location (Clock-In, Load Site, Dump Site, etc.)
  index: number; // Index within the form array
  address?: string; // Address string for the location
}

export interface MapState {
  locations: MapLocation[];
  selectedLocationIndex: number;
  selectedLocationType: string; // Type of currently selected location
  selectedLat: number | null;
  selectedLng: number | null;
  markers: (google.maps.marker.AdvancedMarkerElement | google.maps.Marker)[];
  markersByType: Map<string, (google.maps.marker.AdvancedMarkerElement | google.maps.Marker)[]>; // Markers grouped by type
  markersByIndex: Map<number, google.maps.marker.AdvancedMarkerElement | google.maps.Marker>; // Markers indexed by location index
  locationSettings: Map<
    number,
    {
      lat: number;
      lng: number;
      zoom: number;
      address?: string;
    }
  >; // Store individual coordinates and zoom for each location index
}

@Injectable({
  providedIn: 'root',
})
export class GoogleApiService {
  private http = inject(HttpClient);

  // State management
  private loadingState = signal<Record<string, boolean>>({});
  private errorState = signal<Record<string, string | null>>({});

  // Session token for Places API
  private sessionToken: google.maps.places.AutocompleteSessionToken | undefined;

  // Single shared promise for the Google Maps script load, used by every
  // caller of `loadGoogleMapsApi()`. This avoids a race where one caller
  // (e.g. AppComponent.ngOnInit) appended the <script> tag but it has not
  // executed yet, while a second caller checks the DOM, sees the tag, and
  // resolves prematurely — leading to autocomplete falling back to a legacy
  // API that is also not yet available, and effectively double-firing the
  // request when the script eventually finishes loading.
  private googleMapsLoadPromise: Promise<void> | null = null;

  // Computed properties
  readonly isLoading = computed(() => Object.values(this.loadingState()).some(Boolean));
  readonly hasError = computed(() => Object.values(this.errorState()).some(Boolean));

  private mapState: MapState = {
    locations: [],
    selectedLocationIndex: 0,
    selectedLocationType: '',
    selectedLat: null,
    selectedLng: null,
    markers: [],
    markersByType: new Map(),
    markersByIndex: new Map(),
    locationSettings: new Map(),
  };

  constructor() {
    this.initializeState();
  }

  /**
   * Maps JavaScript API exposes the library on `window.google`. ES modules do not define a bare
   * global `google`, so using `google.maps` throws ReferenceError unless accessed via window.
   */
  private getGoogleMapsApi(): NonNullable<CustomWindow['google']> | undefined {
    if (typeof window === 'undefined') {
      return undefined;
    }
    return (window as CustomWindow).google;
  }

  /**
   * Initializes or resets the autocomplete session.
   * A new session starts when the user begins typing.
   */
  private startNewSession(): void {
    const g = this.getGoogleMapsApi();
    if (g?.maps?.places) {
      this.sessionToken = new g.maps.places.AutocompleteSessionToken();
    }
  }

  /**
   * Get place predictions for autocomplete using the new Places API.
   *
   * Uses `AutocompleteSuggestion.fetchAutocompleteSuggestions` — the proper
   * autocomplete endpoint of the new Places API — which correctly handles
   * partial inputs (including pure numeric prefixes like "1234"). Falls back
   * to the legacy `AutocompleteService` and finally `Geocoder` if the newer
   * APIs are not available in the loaded Maps build.
   */
  getPlacePredictions(input: string): Observable<PlacePrediction[]> {
    const trimmed = (input ?? '').trim();
    if (trimmed.length < AUTOCOMPLETE_MIN_QUERY_LENGTH) {
      return of([]);
    }

    this.setLoading('places', true);
    this.clearError('places');

    if (!this.sessionToken) {
      this.startNewSession();
    }

    return new Observable<PlacePrediction[]>(observer => {
      let innerSub: { unsubscribe(): void } | undefined;

      if (!(window as CustomWindow).google?.maps?.places) {
        innerSub = this.loadGoogleMapsApi().subscribe({
          next: () => {
            this.startNewSession();
            this.performAutocompleteWithNewAPI(trimmed, observer);
          },
          error: (error: unknown) => {
            this.setError('places', error);
            observer.error(error);
            observer.complete();
          },
        });
      } else {
        this.performAutocompleteWithNewAPI(trimmed, observer);
      }

      return () => innerSub?.unsubscribe();
    }).pipe(finalize(() => this.setLoading('places', false)));
  }

  /**
   * Performs autocomplete request using the new Places API
   * (`AutocompleteSuggestion.fetchAutocompleteSuggestions`).
   *
   * Important: the method is a `static` of `AutocompleteSuggestion` and the
   * Google Maps runtime relies on the `this` context being the class itself.
   * Destructuring (`const fn = ns.fetchAutocompleteSuggestions`) loses `this`
   * and causes the call to throw, which previously triggered an unwanted
   * legacy fallback and produced two network requests per keystroke. Always
   * call it through the class.
   */
  private async performAutocompleteWithNewAPI(
    input: string,
    observer: {
      next: (value: PlacePrediction[]) => void;
      error: (error: unknown) => void;
      complete: () => void;
    },
  ): Promise<void> {
    const g = this.getGoogleMapsApi();
    const AutocompleteSuggestionClass = g?.maps?.places?.AutocompleteSuggestion;

    if (!AutocompleteSuggestionClass?.fetchAutocompleteSuggestions) {
      // New API is genuinely unavailable in this Maps build — fall back to
      // the legacy AutocompleteService once. We do NOT fall back on runtime
      // errors of the new API to avoid issuing two requests per keystroke.
      this.performAutocompleteLegacy(input, observer);
      return;
    }

    try {
      const request: google.maps.places.AutocompleteRequest = {
        input,
        includedRegionCodes: ['us'],
        language: 'en',
        region: 'us',
        sessionToken: this.sessionToken,
      };

      const { suggestions } =
        await AutocompleteSuggestionClass.fetchAutocompleteSuggestions(request);

      const mapped: PlacePrediction[] = (suggestions || [])
        .map(s => s.placePrediction)
        .filter((p): p is google.maps.places.PlacePrediction => p !== null)
        .map(prediction => {
          const fullText = prediction.text?.text || '';
          const mainText = prediction.mainText?.text || fullText;
          const secondaryText = prediction.secondaryText?.text || '';

          return {
            place_id: prediction.placeId,
            description: fullText,
            structured_formatting: {
              main_text: mainText,
              secondary_text: secondaryText,
            },
            types: prediction.types || [],
          };
        });

      observer.next(mapped);
      observer.complete();
    } catch (error) {
      this.setError('places', error);
      observer.next([]);
      observer.complete();
    }
  }

  /**
   * Legacy fallback: uses `AutocompleteService.getPlacePredictions` and, if
   * unavailable, the Geocoder. Both still return reasonable predictions for
   * partial inputs.
   */
  private performAutocompleteLegacy(
    input: string,
    observer: {
      next: (value: PlacePrediction[]) => void;
      error: (error: unknown) => void;
      complete: () => void;
    },
  ): void {
    const g = this.getGoogleMapsApi();
    const AutocompleteServiceCtor = g?.maps?.places?.AutocompleteService;

    if (AutocompleteServiceCtor) {
      const service = new AutocompleteServiceCtor();
      service
        .getPlacePredictions({
          input,
          componentRestrictions: { country: 'us' },
          language: 'en',
          region: 'us',
          sessionToken: this.sessionToken,
        })
        .then(response => {
          const predictions = response?.predictions || [];
          const mapped: PlacePrediction[] = predictions.map(p => ({
            place_id: p.place_id,
            description: p.description,
            structured_formatting: {
              main_text: p.structured_formatting?.main_text || p.description,
              secondary_text: p.structured_formatting?.secondary_text || '',
            },
            types: p.types || [],
          }));
          observer.next(mapped);
          observer.complete();
        })
        .catch(() => {
          this.performAutocompleteGeocoderFallback(input, observer);
        });
      return;
    }

    this.performAutocompleteGeocoderFallback(input, observer);
  }

  /**
   * Last-resort fallback: uses Geocoder when neither the new
   * AutocompleteSuggestion API nor the legacy AutocompleteService are
   * available in the loaded Maps build.
   */
  private performAutocompleteGeocoderFallback(
    input: string,
    observer: {
      next: (value: PlacePrediction[]) => void;
      error: (error: unknown) => void;
      complete: () => void;
    },
  ): void {
    const g = this.getGoogleMapsApi();
    if (!g?.maps?.Geocoder) {
      observer.next([]);
      observer.complete();
      return;
    }

    const geocoder = new g.maps.Geocoder();
    geocoder.geocode(
      {
        address: input,
        componentRestrictions: { country: 'US' },
        region: 'us',
      },
      (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
        if (status === g.maps.GeocoderStatus.OK && results && results.length > 0) {
          const mappedPredictions: PlacePrediction[] = results.slice(0, 5).map(result => {
            const addressParts = result.formatted_address.split(',');
            const mainText = addressParts[0]?.trim() || result.formatted_address;
            const secondaryText = addressParts.slice(1).join(',').trim() || '';

            return {
              description: result.formatted_address,
              place_id: result.place_id,
              structured_formatting: {
                main_text: mainText,
                secondary_text: secondaryText,
              },
              types: result.types,
            };
          });
          observer.next(mappedPredictions);
        } else {
          observer.next([]);
        }
        observer.complete();
      },
    );
  }

  /**
   * Get place details by place ID using the new Places API
   */
  getPlaceDetails(placeId: string): Observable<PlaceDetails> {
    if (!placeId) {
      return throwError(() => new Error('Place ID is required'));
    }

    this.setLoading('placeDetails', true);
    this.clearError('placeDetails');

    return new Observable<PlaceDetails>(observer => {
      let innerSub: { unsubscribe(): void } | undefined;

      if (!(window as CustomWindow).google?.maps?.places) {
        innerSub = this.loadGoogleMapsApi().subscribe({
          next: () => this.performPlaceDetails(placeId, observer),
          error: (error: unknown) => {
            this.setError('placeDetails', error);
            observer.error(error);
            observer.complete();
          },
        });
      } else {
        this.performPlaceDetails(placeId, observer);
      }

      return () => innerSub?.unsubscribe();
    }).pipe(
      finalize(() => {
        this.setLoading('placeDetails', false);
        // A session ends with a Place Details request. Clear the token.
        this.sessionToken = undefined;
      }),
    );
  }

  private performPlaceDetails(
    placeId: string,
    observer: {
      next: (value: PlaceDetails) => void;
      error: (error: unknown) => void;
      complete: () => void;
    },
  ): void {
    const g = this.getGoogleMapsApi();
    if (!g?.maps?.places?.Place) {
      observer.error(new Error('Google Maps Places API is not available.'));
      observer.complete();
      return;
    }

    // Use the new `google.maps.places.Place` class as recommended
    const place = new g.maps.places.Place({ id: placeId });

    (async () => {
      try {
        await place.fetchFields({
          fields: ['id', 'formattedAddress', 'addressComponents', 'location'],
        });

        if (!place.id) {
          throw new Error('Place not found.');
        }

        const placeDetails: PlaceDetails = {
          place_id: place.id,
          formatted_address: place.formattedAddress || '',
          address_components:
            place.addressComponents?.map(c => ({
              longText: c.longText || '',
              shortText: c.shortText || '',
              types: c.types || [],
              languageCode: 'en', // Not provided by new API directly
            })) || [],
          location: {
            lat: place.location?.lat() || 0,
            lng: place.location?.lng() || 0,
          },
        };
        observer.next(placeDetails);
      } catch (error) {
        this.setError('placeDetails', error);
        observer.error(error);
      } finally {
        observer.complete();
      }
    })();
  }

  /**
   * Extract address components from place details
   */
  extractAddressComponents(placeDetails: PlaceDetails): {
    streetAddress: string;
    city: string;
    state: string;
    postalCode: string;
  } {
    const getComponent = (type: string, useShortName = false): string => {
      const component = placeDetails.address_components.find(c => c.types.includes(type));
      return component ? (useShortName ? component.shortText : component.longText) : '';
    };

    // Extract the street number and route to create a street address
    const streetNumber = getComponent('street_number');
    const route = getComponent('route');

    let streetAddress = '';

    // Try different approaches to get a street address
    if (streetNumber || route) {
      // Standard approach: combine street number and route
      streetAddress = [streetNumber, route].filter(Boolean).join(' ');
    } else {
      // Fallback approaches for locations without explicit street_number/route
      const premise = getComponent('premise');
      const subpremise = getComponent('subpremise');
      const establishment = getComponent('establishment');
      const pointOfInterest = getComponent('point_of_interest');

      // Try premise first (building/property name)
      if (premise) {
        streetAddress = premise;
      } else if (subpremise) {
        streetAddress = subpremise;
      } else if (establishment) {
        streetAddress = establishment;
      } else if (pointOfInterest) {
        streetAddress = pointOfInterest;
      } else {
        // Last resort: extract first part of formatted address
        const addressParts = placeDetails.formatted_address.split(',');
        streetAddress = addressParts[0]?.trim() || '';
      }
    }

    const city = getComponent('locality');
    const state = getComponent('administrative_area_level_1', true);
    const postalCode = getComponent('postal_code');

    return {
      streetAddress,
      city,
      state,
      postalCode,
    };
  }

  /**
   * Get place details from coordinates using reverse geocoding
   */
  reverseGeocode(lat: number, lng: number): Observable<PlaceDetails> {
    this.setLoading('reverseGeocode', true);
    this.clearError('reverseGeocode');

    return new Observable<PlaceDetails>(observer => {
      let innerSub: { unsubscribe(): void } | undefined;

      if (!(window as CustomWindow).google?.maps?.places) {
        innerSub = this.loadGoogleMapsApi().subscribe({
          next: () => {
            this.performReverseGeocode(lat, lng, observer);
          },
          error: (error: unknown) => {
            this.setError('reverseGeocode', error);
            observer.error(error);
            observer.complete();
          },
        });
      } else {
        this.performReverseGeocode(lat, lng, observer);
      }

      return () => innerSub?.unsubscribe();
    }).pipe(finalize(() => this.setLoading('reverseGeocode', false)));
  }

  /**
   * Performs reverse geocoding request using the Geocoder service.
   */
  private performReverseGeocode(
    lat: number,
    lng: number,
    observer: {
      next: (value: PlaceDetails) => void;
      error: (error: unknown) => void;
      complete: () => void;
    },
  ): void {
    const g = this.getGoogleMapsApi();
    if (!g?.maps?.Geocoder) {
      const error = new Error('Google Maps Geocoder is not available.');
      this.setError('reverseGeocode', error);
      observer.error(error);
      observer.complete();
      return;
    }

    const geocoder = new g.maps.Geocoder();

    geocoder.geocode(
      { location: { lat, lng } },
      (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
        if (status === g.maps.GeocoderStatus.OK && results && results.length > 0) {
          const result = results[0];

          // Convert GeocoderResult to PlaceDetails format
          const placeDetails: PlaceDetails = {
            place_id: result.place_id,
            formatted_address: result.formatted_address,
            address_components: result.address_components.map(component => ({
              longText: component.long_name,
              shortText: component.short_name,
              types: component.types,
              languageCode: 'en',
            })),
            location: {
              lat: result.geometry.location.lat(),
              lng: result.geometry.location.lng(),
            },
          };

          observer.next(placeDetails);
          observer.complete();
        } else {
          const error = new Error(`Reverse geocoding failed: ${status}`);
          this.setError('reverseGeocode', error);
          observer.error(error);
          observer.complete();
        }
      },
    );
  }

  /**
   * Test API connection
   */
  testApiConnection(): Observable<GoogleApiStatus> {
    const hasApiKey = !!environment.googleMapsApiKey;
    return of({
      status: 'OK',
      message: hasApiKey
        ? 'Using Google Maps API with key'
        : 'Using Google Maps API free tier (limited functionality)',
      timestamp: new Date().toISOString(),
      googleApiKey: hasApiKey ? 'Configured' : 'Using free tier',
    });
  }

  /**
   * Load Google Maps API dynamically.
   *
   * Concurrency-safe: every caller subscribes to the same load promise, so a
   * second caller never resolves prematurely while the <script> tag is still
   * loading.
   */
  loadGoogleMapsApi(): Observable<boolean> {
    if ((window as CustomWindow).google?.maps?.places) {
      return of(true);
    }

    if (!this.googleMapsLoadPromise) {
      this.googleMapsLoadPromise = this.createGoogleMapsLoadPromise();
    }

    return from(this.googleMapsLoadPromise).pipe(map(() => true));
  }

  private createGoogleMapsLoadPromise(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.setLoading('googleApi', true);
      this.clearError('googleApi');

      const scriptId = 'google-maps-script';
      const existing = document.getElementById(scriptId) as HTMLScriptElement | null;

      const onReady = () => {
        this.setLoading('googleApi', false);
        this.startNewSession();
        resolve();
      };

      const onFail = (reason: string) => {
        this.setError('googleApi', reason);
        this.setLoading('googleApi', false);
        this.googleMapsLoadPromise = null;
        delete (window as CustomWindow).googleMapsReady;
        reject(reason);
      };

      // Wire the global callback regardless of whether the <script> already
      // exists. The Maps loader invokes `googleMapsReady` once the API is
      // fully initialised, which is the only reliable signal.
      (window as CustomWindow).googleMapsReady = onReady;

      if (existing) {
        existing.addEventListener('error', () => onFail('Failed to load Google Maps API.'));
        // If the script element is already present and Maps somehow finished
        // loading without our callback being attached, fast-path through.
        if ((window as CustomWindow).google?.maps?.places) {
          onReady();
        }
        return;
      }

      const script = document.createElement('script');
      script.id = scriptId;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.googleMapsApiKey}&libraries=places,marker,geometry&language=en&region=US&loading=async&callback=googleMapsReady`;
      script.async = true;
      script.defer = true;
      script.onerror = () => onFail('Failed to load Google Maps API.');

      document.head.appendChild(script);
    });
  }

  // Private methods

  private initializeState(): void {
    this.loadingState.set({
      places: false,
      placeDetails: false,
      reverseGeocode: false,
      googleApi: false,
    });
    this.errorState.set({
      places: null,
      placeDetails: null,
      reverseGeocode: null,
      googleApi: null,
    });
  }

  private mapAutocompleteResponse(response: GoogleAutocompleteResponse): PlacePrediction[] {
    if (!response?.predictions || response.status !== 'OK') {
      return [];
    }

    return response.predictions.map(prediction => ({
      place_id: prediction.place_id,
      description: prediction.description,
      structured_formatting: {
        main_text: prediction.structured_formatting?.main_text || prediction.description,
        secondary_text: prediction.structured_formatting?.secondary_text || '',
      },
      types: prediction.types || [],
    }));
  }

  private mapPlaceDetailsResponse(response: GooglePlaceDetailsResponse): PlaceDetails {
    if (!response?.result || response.status !== 'OK') {
      throw new Error('Invalid place details response');
    }

    return {
      place_id: response.result.place_id,
      formatted_address: response.result.formatted_address,
      address_components: response.result.address_components.map(component => ({
        longText: component.long_name,
        shortText: component.short_name,
        types: component.types,
        languageCode: 'en',
      })),
      location: response.result.geometry?.location
        ? {
            lat: response.result.geometry.location.lat,
            lng: response.result.geometry.location.lng,
          }
        : undefined,
    };
  }

  private setLoading(key: string, value: boolean): void {
    this.loadingState.update(state => ({ ...state, [key]: value }));
  }

  private setError(key: string, error: unknown): void {
    const message = this.getErrorMessage(error);
    this.errorState.update(state => ({ ...state, [key]: message }));
  }

  private clearError(key: string): void {
    this.errorState.update(state => ({ ...state, [key]: null }));
  }

  private getErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      return error.message;
    }
    if (error instanceof Error) {
      return error.message;
    }
    return 'An unknown error occurred.';
  }

  // Global map state management
  getMapState(): MapState {
    return this.mapState;
  }

  setMapState(state: Partial<MapState>): void {
    this.mapState = { ...this.mapState, ...state };
  }

  updateLocations(locations: MapLocation[], selectedLocationIndex: number): void {
    this.mapState.locations = locations;
    this.mapState.selectedLocationIndex = selectedLocationIndex;

    // Update selected location type
    const selectedLocation = locations[selectedLocationIndex];
    if (selectedLocation) {
      this.mapState.selectedLocationType = selectedLocation.type;
    }

    // Save coordinates only if they are actually provided (not null)
    if (selectedLocation && selectedLocation.lat !== null && selectedLocation.lng !== null) {
      this.mapState.selectedLat = selectedLocation.lat;
      this.mapState.selectedLng = selectedLocation.lng;
    }

    // Create and add markers for locations with coordinates
    this.createAndAddMarkers(locations);
  }

  /**
   * Create and add markers for locations with valid coordinates
   */
  private createAndAddMarkers(locations: MapLocation[]): void {
    // Clear existing markers first
    this.clearMarkers();

    locations.forEach((location, index) => {
      if (location.lat !== null && location.lng !== null) {
        this.createMarkerForLocation(location, index);
      }
    });
  }

  /**
   * Create a marker for a specific location
   */
  private createMarkerForLocation(location: MapLocation, index: number): void {
    if (!location.lat || !location.lng) {
      return;
    }

    const g = this.getGoogleMapsApi();
    if (!g?.maps?.LatLng || !g.maps.marker?.AdvancedMarkerElement) {
      return;
    }

    const position = new g.maps.LatLng(location.lat!, location.lng!);

    // Create marker with custom styling based on location type
    const marker = new g.maps.marker.AdvancedMarkerElement({
      position: position,
      title: location.label,
      map: null, // Will be set by the map component
      content: this.createMarkerContent(location.type),
    });

    // Add marker to state
    this.addMarkerWithIndex(marker, index);
  }

  /**
   * Create marker content based on location type
   */
  private createMarkerContent(locationType: string): HTMLElement {
    const markerElement = document.createElement('div');
    markerElement.className = 'custom-marker';

    // Create a simple colored circle instead of using SVG images
    const circle = document.createElement('div');
    circle.style.width = '24px';
    circle.style.height = '24px';
    circle.style.borderRadius = '50%';
    circle.style.border = '2px solid white';
    circle.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';

    // Set color based on location type
    switch (locationType) {
      case 'Clock-In':
      case 'Clock-Out':
        circle.style.backgroundColor = '#52c41a'; // Green for clock in/out
        break;
      case 'Load':
        circle.style.backgroundColor = '#1890ff'; // Blue for load
        break;
      case 'Dump':
        circle.style.backgroundColor = '#fa8c16'; // Orange for dump
        break;
      default:
        circle.style.backgroundColor = '#722ed1'; // Purple for default
    }

    markerElement.appendChild(circle);
    return markerElement;
  }

  clearMarkers(): void {
    const g = this.getGoogleMapsApi();
    this.mapState.markers.forEach(marker => {
      if (g?.maps && marker instanceof g.maps.Marker) {
        marker.setMap(null);
      } else if (g?.maps?.marker && marker instanceof g.maps.marker.AdvancedMarkerElement) {
        marker.map = null;
      }
    });
    this.mapState.markers = [];
    this.mapState.markersByIndex.clear();
  }

  addMarker(marker: google.maps.marker.AdvancedMarkerElement | google.maps.Marker): void {
    this.mapState.markers.push(marker);
  }

  // New method to add marker with location index
  addMarkerWithIndex(
    marker: google.maps.marker.AdvancedMarkerElement | google.maps.Marker,
    locationIndex: number,
  ): void {
    // Remove the existing marker for this location index if it exists
    this.removeMarkerByIndex(locationIndex);

    // Add a new marker
    this.mapState.markers.push(marker);
    this.mapState.markersByIndex.set(locationIndex, marker);
  }

  // New method to remove marker by location index
  removeMarkerByIndex(locationIndex: number): void {
    const existingMarker = this.mapState.markersByIndex.get(locationIndex);
    if (existingMarker) {
      const g = this.getGoogleMapsApi();
      // Remove from map
      if (g?.maps && existingMarker instanceof g.maps.Marker) {
        existingMarker.setMap(null);
      } else if (g?.maps?.marker && existingMarker instanceof g.maps.marker.AdvancedMarkerElement) {
        existingMarker.map = null;
      }

      // Remove from arrays
      const markerIndex = this.mapState.markers.indexOf(existingMarker);
      if (markerIndex > -1) {
        this.mapState.markers.splice(markerIndex, 1);
      }

      this.mapState.markersByIndex.delete(locationIndex);
    }
  }

  // New method to get marker by location index
  getMarkerByIndex(
    locationIndex: number,
  ): google.maps.marker.AdvancedMarkerElement | google.maps.Marker | null {
    return this.mapState.markersByIndex.get(locationIndex) || null;
  }

  // New method to check if marker exists for location index
  hasMarkerForIndex(locationIndex: number): boolean {
    return this.mapState.markersByIndex.has(locationIndex);
  }

  getMarkers(): (google.maps.marker.AdvancedMarkerElement | google.maps.Marker)[] {
    return this.mapState.markers;
  }

  setMarkers(markers: (google.maps.marker.AdvancedMarkerElement | google.maps.Marker)[]): void {
    this.mapState.markers = markers;
  }

  updateSelectedLocation(lat: number, lng: number): void {
    this.mapState.selectedLat = lat;
    this.mapState.selectedLng = lng;
  }

  // Add marker to group by type
  addMarkerToType(
    marker: google.maps.marker.AdvancedMarkerElement | google.maps.Marker,
    type: string,
  ): void {
    if (!this.mapState.markersByType.has(type)) {
      this.mapState.markersByType.set(type, []);
    }
    this.mapState.markersByType.get(type)!.push(marker);
  }

  // Get markers by type
  getMarkersByType(
    type: string,
  ): (google.maps.marker.AdvancedMarkerElement | google.maps.Marker)[] {
    return this.mapState.markersByType.get(type) || [];
  }

  // Clear markers of specific type
  clearMarkersByType(type: string): void {
    const g = this.getGoogleMapsApi();
    const markersToRemove = this.mapState.markersByType.get(type) || [];
    markersToRemove.forEach(marker => {
      if (g?.maps && marker instanceof g.maps.Marker) {
        marker.setMap(null);
      } else if (g?.maps?.marker && marker instanceof g.maps.marker.AdvancedMarkerElement) {
        marker.map = null;
      }
    });
    this.mapState.markersByType.delete(type);
  }

  // Clear state when navigating to another page
  clearMapState(): void {
    this.clearMarkers();
    this.mapState = {
      locations: [],
      selectedLocationIndex: 0,
      selectedLocationType: '',
      selectedLat: null,
      selectedLng: null,
      markers: [],
      markersByType: new Map(),
      markersByIndex: new Map(),
      locationSettings: new Map(),
    };
  }

  // New methods for individual location settings
  saveLocationSettings(
    locationIndex: number,
    lat: number,
    lng: number,
    zoom: number,
    address?: string,
  ): void {
    this.mapState.locationSettings.set(locationIndex, {
      lat,
      lng,
      zoom,
      address,
    });
  }

  getLocationSettings(
    locationIndex: number,
  ): { lat: number; lng: number; zoom: number; address?: string } | null {
    const settings = this.mapState.locationSettings.get(locationIndex);
    return settings || null;
  }

  hasLocationSettings(locationIndex: number): boolean {
    const hasSettings = this.mapState.locationSettings.has(locationIndex);
    return hasSettings;
  }

  clearLocationSettings(locationIndex: number): void {
    this.mapState.locationSettings.delete(locationIndex);
  }

  // New method to trigger map update from external components
  triggerMapUpdate(): void {
    // MapModal will call this if it's listening for updates
    // For now, we'll just log the trigger

    // Emit a custom event that MapModal can listen to
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('mapStateUpdated', {
          detail: {
            locations: this.mapState.locations,
            selectedLocationIndex: this.mapState.selectedLocationIndex,
          },
        }),
      );
    }
  }
}
